import {get, liftA, pick} from '@cullylarson/f'
import {getJwtPayload} from '@@client/lib/jwt'
import {getAccountInfo} from '@@client/lib/auth'
import {getPaypalReturnUrl, getPaypalCancelUrl} from './paypal'
import {getAffirmReturnUrl, getAffirmCancelUrl} from './affirm'
import {getCart, getItemNames} from '@@client/cart/cart-repo'
import {addNotificationError, addNotificationNotice} from '@@client/notification/actions'
import {SHIPPING_METHOD_IN_STORE, SHIPPING_METHOD_HOME} from '@@client/checkout/checkout-utils'
import * as api from './api'
import actionTypes from './action-types'

const contactInfoParamErorsFields = ['contactName', 'contactEmail', 'contactPhone']
const getContactInfoParamErrors = pick(contactInfoParamErorsFields)
const hasContactInfoParamErrors = paramErrors => Object.values(getContactInfoParamErrors(paramErrors)).filter(x => x && x.length > 0).length > 0

const hasParamError = (paramErrors, paramNames) => {
    paramNames = liftA(paramNames)

    for(const paramName of paramNames) {
        if(get(paramName, [], paramErrors).length > 0) return true
    }

    return false
}

export function markGoToCart() {
    return {
        type: actionTypes.MARK_GO_TO_CART,
    }
}

export function clearGoToCart() {
    return {
        type: actionTypes.CLEAR_GO_TO_CART,
    }
}

export function clearShippingTokens() {
    return {
        type: actionTypes.CLEAR_SHIPPING_TOKENS,
    }
}

export function clearContactInfoErrors() {
    return {
        type: actionTypes.CLEAR_CONTACT_INFO_ERRORS,
    }
}

export function contactInfoFailure(errors, paramErrors) {
    return {
        type: actionTypes.CONTACT_INFO_FAILURE,
        errors,
        paramErrors,
    }
}

export function choosePickupStore(storeId) {
    return {
        type: actionTypes.CHOOSE_PICKUP_STORE,
        storeId,
    }
}

export function fillBillingAddressFromShippingAddress() {
    return {
        type: actionTypes.FILL_BILLING_ADDRESS_FROM_SHIPPING_ADDRESS,
    }
}

export function respondCheckoutTokenParamError(paramErrors) {
    return (dispatch) => {
        // don't do anything unless there are param errors for the checkoutToken
        if(!paramErrors || !paramErrors.checkoutToken || !paramErrors.checkoutToken.length) return

        dispatch(markGoToCart())
        dispatch(resetCheckout())
        dispatch(addNotificationNotice('Your checkout session has expired. Click the Checkout button to get started again.', {autoDismiss: true, dismissTimeout: 12000}))
    }
}

export function setCheckoutField(fieldName, value) {
    return {
        type: actionTypes.SET_CHECKOUT_FIELD,
        fieldName,
        value,
    }
}

export function setShippingMethod(shippingMethod) {
    return {
        type: actionTypes.SET_SHIPPING_METHOD,
        shippingMethod,
    }
}

export function setPaymentMethod(paymentMethod) {
    return {
        type: actionTypes.SET_PAYMENT_METHOD,
        paymentMethod,
    }
}

export function resetCheckout(preserveOrderNumber = false) {
    return {
        type: actionTypes.RESET_CHECKOUT,
        preserveOrderNumber,
    }
}

export function setIsGuest(isGuest) {
    return {
        type: actionTypes.SET_IS_GUEST,
        isGuest,
    }
}

function storePickupOptionsRequest() {
    return {
        type: actionTypes.STORE_PICKUP_OPTIONS_REQUEST,
    }
}

function storePickupOptionsSuccess(result) {
    return {
        type: actionTypes.STORE_PICKUP_OPTIONS_SUCCESS,
        result,
    }
}

function storePickupOptionsFailure(errors) {
    return {
        type: actionTypes.STORE_PICKUP_OPTIONS_FAILURE,
        errors,
    }
}

export function getStorePickupOptions(tokenCache) {
    return (dispatch, getState) => {
        const state = getState()

        dispatch(storePickupOptionsRequest())

        return api.getStorePickupOptions(tokenCache, state.config.orders.apiUrl, state.checkout.info.checkoutToken)
            .then(result => {
                if(result.success) {
                    dispatch(storePickupOptionsSuccess(result.data))
                    return true
                }
                else {
                    dispatch(respondCheckoutTokenParamError(result.paramErrors))
                    dispatch(storePickupOptionsFailure(result.errors))
                    return false
                }
            })
            .catch(_ => {
                dispatch(storePickupOptionsFailure(['Something went wrong and in-store pickup options could not be fetched. Please try again.']))
                return false
            })
    }
}

function canHomeShipRequest() {
    return {
        type: actionTypes.CAN_HOME_SHIP_REQUEST,
    }
}

function canHomeShipSuccess(canHomeShip, willCallOnlyInventoryIds) {
    return {
        type: actionTypes.CAN_HOME_SHIP_SUCCESS,
        canHomeShip,
        willCallOnlyInventoryIds,
    }
}

function canHomeShipFailure(errors) {
    return {
        type: actionTypes.CAN_HOME_SHIP_FAILURE,
        errors,
    }
}

export function getCanHomeShip(tokenCache) {
    return (dispatch, getState) => {
        const state = getState()

        dispatch(canHomeShipRequest())

        api.getCanHomeShip(tokenCache, state.config.orders.apiUrl, state.checkout.info.checkoutToken)
            .then(result => {
                if(result.success) {
                    dispatch(canHomeShipSuccess(result.canHomeShip, result.willCallOnlyInventoryIds))
                }
                else {
                    dispatch(respondCheckoutTokenParamError(result.paramErrors))
                    dispatch(canHomeShipFailure(result.errors))
                }
            })
            .catch(_ => dispatch(canHomeShipFailure(['Something went wrong and home shipping options could not be fetched. Please try again.'])))
    }
}

function homeShippingEstimateRequest() {
    return {
        type: actionTypes.HOME_SHIPPING_ESTIMATE_REQUEST,
    }
}

function homeShippingEstimateSuccess(estimateTotal, estimateToken, estimateTokenPayload) {
    return {
        type: actionTypes.HOME_SHIPPING_ESTIMATE_SUCCESS,
        estimateTotal,
        estimateToken,
        estimateTokenPayload,
    }
}

function homeShippingEstimateFailure(errors, paramErrors = {}) {
    return {
        type: actionTypes.HOME_SHIPPING_ESTIMATE_FAILURE,
        errors,
        paramErrors,
    }
}

export function getHomeShippingEstimate(tokenCache) {
    return (dispatch, getState) => {
        const state = getState()

        const checkoutInfo = state.checkout.info

        const shippingAddress = {
            shippingName: checkoutInfo.shippingName,
            shippingCompany: checkoutInfo.shippingCompany,
            shippingAddress1: checkoutInfo.shippingAddress1,
            shippingAddress2: checkoutInfo.shippingAddress2,
            shippingCity: checkoutInfo.shippingCity,
            shippingState: checkoutInfo.shippingState,
            shippingCountry: checkoutInfo.shippingCountry,
            shippingZip: checkoutInfo.shippingZip,
        }

        dispatch(homeShippingEstimateRequest())

        return api.getHomeShippingEstimate(tokenCache, state.config.orders.apiUrl, shippingAddress, checkoutInfo.checkoutToken)
            .then(result => {
                if(result.success) {
                    dispatch(homeShippingEstimateSuccess(result.estimateTotal, result.estimateToken, getJwtPayload(result.estimateToken, null, state.config.orders.claimsNamespace)))
                }
                else {
                    dispatch(respondCheckoutTokenParamError(result.paramErrors))
                    dispatch(homeShippingEstimateFailure(result.errors, result.paramErrors))
                }

                return result.success
            })
            .catch(_ => {
                dispatch(homeShippingEstimateFailure(['Something went wrong and shipping cost could not be fetched. Please try again.']))

                return false
            })
    }
}

function storePickupRequest() {
    return {
        type: actionTypes.STORE_PICKUP_REQUEST,
    }
}

function storePickupSuccess(storePickupToken, storePickupTokenPayload) {
    return {
        type: actionTypes.STORE_PICKUP_SUCCESS,
        storePickupToken,
        storePickupTokenPayload,
    }
}

function storePickupFailure(errors, paramErrors = {}) {
    return {
        type: actionTypes.STORE_PICKUP_FAILURE,
        errors,
        paramErrors,
    }
}

export function getStorePickup(tokenCache) {
    return (dispatch, getState) => {
        const state = getState()

        const checkoutInfo = state.checkout.info

        dispatch(storePickupRequest())

        return api.getStorePickup(tokenCache, state.config.orders.apiUrl, checkoutInfo.checkoutToken, checkoutInfo.storePickupOptionsToken, checkoutInfo.pickupStoreId)
            .then(result => {
                if(result.success) {
                    dispatch(storePickupSuccess(result.storePickupToken, getJwtPayload(result.storePickupToken, null, state.config.orders.claimsNamespace)))
                }
                else {
                    dispatch(respondCheckoutTokenParamError(result.paramErrors))
                    dispatch(storePickupFailure(result.errors, result.paramErrors))
                }

                return result.success
            })
            .catch(_ => {
                dispatch(storePickupFailure(['Something went wrong and the selected store could not be verified. Please try again.']))

                return false
            })
    }
}

function initializeCheckoutRequest() {
    return (dispatch) => {
        dispatch(resetCheckout())
        dispatch({type: actionTypes.INITIALIZE_CHECKOUT_REQUEST})
    }
}

function initializeCheckoutSuccess(checkoutToken, checkoutTokenPayload) {
    return {
        type: actionTypes.INITIALIZE_CHECKOUT_SUCCESS,
        checkoutToken,
        checkoutTokenPayload,
    }
}

function initializeCheckoutFailure(errors, paramErrors) {
    return (dispatch, getState) => {
        const cart = getCart()

        // show the erorrs as notifications
        errors
            .map(x => {
                // if any of the messages relate to specific inventory items, then include those items in the message
                if(x.message && x.inventoryIds) {
                    const itemNames = getItemNames(cart, x.inventoryIds).join(', ')
                    return `${x.message} (${itemNames})`
                }
                else {
                    return x
                }
            })
            .forEach(x => {
                const message = x.message || x

                dispatch(addNotificationError(message, {autoDismiss: true, dismissTimeout: 12000}))
            })

        dispatch({
            type: actionTypes.INITIALIZE_CHECKOUT_FAILURE,
            errors,
            paramErrors,
        })
    }
}

export function intializeCheckout(tokenCache) {
    return (dispatch, getState) => {
        const state = getState()

        const cart = getCart()

        const inventoryIds = Object.keys(cart.items)

        const offerInfos = Object.entries(cart.offers)
            .map(([inventoryId, offerPrice]) => ({inventoryId, offerPrice}))

        const warrantyInfos = Object.entries(cart.warranties)
            .map(([inventoryId, warrantyId]) => ({inventoryId, warrantyId}))

        dispatch(resetCheckout())
        dispatch(initializeCheckoutRequest())

        return api.getCheckoutToken(tokenCache, state.config.orders.apiUrl, inventoryIds, offerInfos, warrantyInfos)
            .then(result => {
                if(result.success) {
                    dispatch(initializeCheckoutSuccess(result.checkoutToken, getJwtPayload(result.checkoutToken, null, state.config.orders.claimsNamespace)))
                }
                else {
                    dispatch(initializeCheckoutFailure(result.errors, result.paramErrors))
                }

                return result.success
            })
            .catch(_ => {
                dispatch(initializeCheckoutFailure(['Something went wrong and the checkout process could not be started. Please try again.']))

                return false
            })
    }
}

function salesTaxRequest() {
    return {
        type: actionTypes.SALES_TAX_REQUEST,
    }
}

function salesTaxSuccess(salesTaxToken, salesTaxTokenPayload) {
    return {
        type: actionTypes.SALES_TAX_SUCCESS,
        salesTaxToken,
        salesTaxTokenPayload,
    }
}

function salesTaxFailure(errors, paramErrors = {}) {
    return (dispatch, getState) => {
        if(hasParamError(paramErrors, 'shippingToken')) {
            dispatch(clearShippingTokens())
            errors = errors.concat(['Your shipping estimate has expired. Please try again.'])
        }

        dispatch({
            type: actionTypes.SALES_TAX_FAILURE,
            errors,
        })
    }
}

export function getSalesTax(tokenCache) {
    return (dispatch, getState) => {
        const state = getState()

        const checkoutInfo = state.checkout.info

        const params = {
            checkoutToken: checkoutInfo.checkoutToken,
            shippingToken: checkoutInfo.shippingMethod === SHIPPING_METHOD_IN_STORE
                ? checkoutInfo.storePickupToken
                : (checkoutInfo.shippingMethod === SHIPPING_METHOD_HOME
                    ? checkoutInfo.homeShippingEstimateToken
                    : null
                ),
        }

        dispatch(salesTaxRequest())

        return api.getSalesTax(tokenCache, state.config.orders.apiUrl, params)
            .then(result => {
                if(result.success) {
                    dispatch(salesTaxSuccess(result.salesTaxToken, getJwtPayload(result.salesTaxToken, null, state.config.orders.claimsNamespace)))
                }
                else {
                    dispatch(respondCheckoutTokenParamError(result.paramErrors))
                    dispatch(salesTaxFailure(result.errors, result.paramErrors))
                }

                return result.success
            })
            .catch(_ => {
                dispatch(salesTaxFailure(['Something went wrong and sales tax could not be fetched for this order. Please try again.']))

                return false
            })
    }
}

function payWithBandrAccountRequest() {
    return {
        type: actionTypes.PAY_WITH_BANDR_ACCOUNT_REQUEST,
    }
}

function payWithBandrAccountSuccess(paymentToken, paymentTokenPayload) {
    return {
        type: actionTypes.PAY_WITH_BANDR_ACCOUNT_SUCCESS,
        paymentToken,
        paymentTokenPayload,
    }
}

function payWithBandrAccountFailure(errors, paramErrors = {}) {
    return (dispatch, getState) => {
        if(hasParamError(paramErrors, 'salesTaxToken')) {
            dispatch(salesTaxFailure(['Your shipping estimate has expired. Please try again.']))
        }

        dispatch({
            type: actionTypes.PAY_WITH_BANDR_ACCOUNT_FAILURE,
            errors,
            paramErrors,
        })
    }
}

export function payWithBandrAccount(tokenCache) {
    return (dispatch, getState) => {
        const state = getState()

        const checkoutInfo = state.checkout.info

        const params = {
            giftCardNumber: checkoutInfo.giftCardNumber,
            giftCardAmount: checkoutInfo.giftCardAmount,
            bandrAccountName: checkoutInfo.bandrAccountName,
            bandrAccountNumber: checkoutInfo.bandrAccountNumber,
            bandrAccountPoNumber: checkoutInfo.bandrAccountPoNumber,
        }

        dispatch(payWithBandrAccountRequest())

        return api.payWithBandrAccount(tokenCache, state.config.orders.apiUrl, checkoutInfo.checkoutToken, checkoutInfo.salesTaxToken, params)
            .then(result => {
                if(result.success) {
                    dispatch(payWithBandrAccountSuccess(result.bandrAccountPaymentToken, getJwtPayload(result.bandrAccountPaymentToken, null, state.config.orders.claimsNamespace)))
                }
                else {
                    dispatch(respondCheckoutTokenParamError(result.paramErrors))
                    dispatch(payWithBandrAccountFailure(result.errors, result.paramErrors))
                }

                return result.success
            })
            .catch(_ => {
                dispatch(payWithBandrAccountFailure(['Something went wrong and your B&R account could not be verified. Please try again.']))

                return false
            })
    }
}

function payWithPaypalRequest() {
    return {
        type: actionTypes.PAY_WITH_PAYPAL_REQUEST,
    }
}

function payWithPaypalSuccess(paymentToken, paymentTokenPayload) {
    return {
        type: actionTypes.PAY_WITH_PAYPAL_SUCCESS,
        paymentToken,
        paymentTokenPayload,
    }
}

function payWithPaypalFailure(errors, paramErrors = {}) {
    return (dispatch, getState) => {
        if(hasParamError(paramErrors, 'salesTaxToken')) {
            dispatch(salesTaxFailure(['Your shipping estimate has expired. Please try again.']))
        }

        // this would be bad since it means we didn't send the URLs correctly, but at least we can tell the user something
        if(!errors.length && hasParamError(paramErrors, ['paypalReturnUrl', 'paypalCancelUrl'])) {
            errors = ['Something went wrong while contacting PayPal. Please try again.']
        }

        dispatch({
            type: actionTypes.PAY_WITH_PAYPAL_FAILURE,
            errors,
            paramErrors,
        })
    }
}

export function payWithPaypal(tokenCache) {
    return (dispatch, getState) => {
        const state = getState()

        const checkoutInfo = state.checkout.info

        const params = {
            giftCardNumber: checkoutInfo.giftCardNumber,
            giftCardAmount: checkoutInfo.giftCardAmount,
            paypalReturnUrl: getPaypalReturnUrl(),
            paypalCancelUrl: getPaypalCancelUrl(),
        }

        dispatch(payWithPaypalRequest())

        return api.payWithPaypal(tokenCache, state.config.orders.apiUrl, checkoutInfo.checkoutToken, checkoutInfo.salesTaxToken, params)
            .then(result => {
                if(result.success) {
                    dispatch(payWithPaypalSuccess(result.paypalPaymentToken, getJwtPayload(result.paypalPaymentToken, null, state.config.orders.claimsNamespace)))
                }
                else {
                    dispatch(respondCheckoutTokenParamError(result.paramErrors))
                    dispatch(payWithPaypalFailure(result.errors, result.paramErrors))
                }

                return result.success
            })
            .catch(_ => {
                dispatch(payWithPaypalFailure(['Something went wrong while contacting PayPal. Please try again.']))

                return false
            })
    }
}

function payWithAffirmRequest() {
    return {
        type: actionTypes.PAY_WITH_AFFIRM_REQUEST,
    }
}

function payWithAffirmSuccess(paymentToken, paymentTokenPayload) {
    return {
        type: actionTypes.PAY_WITH_AFFIRM_SUCCESS,
        paymentToken,
        paymentTokenPayload,
    }
}

function payWithAffirmFailure(errors, paramErrors = {}) {
    return (dispatch, getState) => {
        if(hasParamError(paramErrors, 'salesTaxToken')) {
            dispatch(salesTaxFailure(['Your shipping estimate has expired. Please try again.']))
        }

        // this would be bad since it means we didn't send the URLs correctly, but at least we can tell the user something
        if(!errors.length && hasParamError(paramErrors, ['affirmReturnUrl', 'affirmCancelUrl'])) {
            errors = ['Something went wrong while contacting Affirm. Please try again.']
        }

        dispatch({
            type: actionTypes.PAY_WITH_AFFIRM_FAILURE,
            errors,
            paramErrors,
        })
    }
}

export function payWithAffirm(tokenCache) {
    return (dispatch, getState) => {
        const state = getState()

        const checkoutInfo = state.checkout.info

        const params = {
            giftCardNumber: checkoutInfo.giftCardNumber,
            giftCardAmount: checkoutInfo.giftCardAmount,
            affirmReturnUrl: getAffirmReturnUrl(),
            affirmCancelUrl: getAffirmCancelUrl(),
            billingName: checkoutInfo.billingName,
            billingAddress1: checkoutInfo.billingAddress1,
            billingAddress2: checkoutInfo.billingAddress2,
            billingCity: checkoutInfo.billingCity,
            billingState: checkoutInfo.billingState,
            billingCountry: checkoutInfo.billingCountry,
            billingZip: checkoutInfo.billingZip,
            billingEmail: checkoutInfo.billingEmail,
            billingPhone: checkoutInfo.billingPhone,
            ...(checkoutInfo.shippingMethod === SHIPPING_METHOD_HOME
                ? {
                    shippingName: checkoutInfo.shippingName,
                    shippingAddress1: checkoutInfo.shippingAddress1,
                    shippingAddress2: checkoutInfo.shippingAddress2,
                    shippingCity: checkoutInfo.shippingCity,
                    shippingState: checkoutInfo.shippingState,
                    shippingCountry: checkoutInfo.shippingCountry,
                    shippingZip: checkoutInfo.shippingZip,
                }
                : {
                    shippingName: checkoutInfo.billingName,
                    shippingAddress1: checkoutInfo.billingAddress1,
                    shippingAddress2: checkoutInfo.billingAddress2,
                    shippingCity: checkoutInfo.billingCity,
                    shippingState: checkoutInfo.billingState,
                    shippingCountry: checkoutInfo.billingCountry,
                    shippingZip: checkoutInfo.billingZip,
                }
            ),
        }

        dispatch(payWithAffirmRequest())

        return api.payWithAffirm(tokenCache, state.config.orders.apiUrl, checkoutInfo.checkoutToken, checkoutInfo.salesTaxToken, params)
            .then(result => {
                if(result.success) {
                    dispatch(payWithAffirmSuccess(result.affirmPaymentToken, getJwtPayload(result.affirmPaymentToken, null, state.config.orders.claimsNamespace)))
                }
                else {
                    dispatch(respondCheckoutTokenParamError(result.paramErrors))
                    dispatch(payWithAffirmFailure(result.errors, result.paramErrors))
                }

                return result.success
            })
            .catch(_ => {
                dispatch(payWithAffirmFailure(['Something went wrong while contacting Affirm. Please try again.']))

                return false
            })
    }
}

function payWithCreditCardRequest() {
    return {
        type: actionTypes.PAY_WITH_CREDIT_CARD_REQUEST,
    }
}

function payWithCreditCardSuccess(paymentToken, paymentTokenPayload) {
    return {
        type: actionTypes.PAY_WITH_CREDIT_CARD_SUCCESS,
        paymentToken,
        paymentTokenPayload,
    }
}

function payWithCreditCardFailure(errors, paramErrors = {}) {
    return (dispatch, getState) => {
        if(hasParamError(paramErrors, 'salesTaxToken')) {
            dispatch(salesTaxFailure(['Your shipping estimate has expired. Please try again.']))
        }

        dispatch({
            type: actionTypes.PAY_WITH_CREDIT_CARD_FAILURE,
            errors,
            paramErrors,
        })
    }
}

export function payWithCreditCard(tokenCache, creditCardTransient) {
    return (dispatch, getState) => {
        const state = getState()

        const checkoutInfo = state.checkout.info

        const params = {
            giftCardNumber: checkoutInfo.giftCardNumber,
            giftCardAmount: checkoutInfo.giftCardAmount,
            creditCardTransient,
            creditCardName: checkoutInfo.creditCardName,
            creditCardExpirationMonth: checkoutInfo.creditCardExpirationMonth,
            creditCardExpirationYear: checkoutInfo.creditCardExpirationYear,
            billingName: checkoutInfo.creditCardName,
            billingAddress1: checkoutInfo.billingAddress1,
            billingAddress2: checkoutInfo.billingAddress2,
            billingCity: checkoutInfo.billingCity,
            billingState: checkoutInfo.billingState,
            billingCountry: checkoutInfo.billingCountry,
            billingZip: checkoutInfo.billingZip,
        }

        dispatch(payWithCreditCardRequest())

        return api.payWithCreditCard(tokenCache, state.config.orders.apiUrl, checkoutInfo.checkoutToken, checkoutInfo.salesTaxToken, params)
            .then(result => {
                if(result.success) {
                    dispatch(payWithCreditCardSuccess(result.creditCardPaymentToken, getJwtPayload(result.creditCardPaymentToken, null, state.config.orders.claimsNamespace)))
                }
                else {
                    dispatch(respondCheckoutTokenParamError(result.paramErrors))
                    dispatch(payWithCreditCardFailure(result.errors, result.paramErrors))
                }

                return result.success
            })
            .catch(_ => {
                dispatch(payWithCreditCardFailure(['Something went wrong while verifying your credit card information. Please try again.']))

                return false
            })
    }
}

function getCreditCardKeyRequest() {
    return {
        type: actionTypes.GET_CREDIT_CARD_KEY_REQUEST,
    }
}

function getCreditCardKeySuccess(creditCardKey, creditCardKeyPayload, creditCardClientLibrary) {
    return {
        type: actionTypes.GET_CREDIT_CARD_KEY_SUCCESS,
        creditCardKey,
        creditCardKeyPayload,
        creditCardClientLibrary,
    }
}

function getCreditCardKeyFailure(errors, paramErrors = {}) {
    return (dispatch, getState) => {
        if(hasParamError(paramErrors, 'salesTaxToken')) {
            dispatch(salesTaxFailure(['Your shipping estimate has expired. Please try again.']))
        }

        dispatch({
            type: actionTypes.GET_CREDIT_CARD_KEY_FAILURE,
            errors,
            paramErrors,
        })
    }
}

export function getCreditCardKey(tokenCache) {
    return (dispatch, getState) => {
        const state = getState()

        const checkoutInfo = state.checkout.info

        dispatch(getCreditCardKeyRequest())

        return api.getCreditCardKey(tokenCache, state.config.orders.apiUrl, checkoutInfo.checkoutToken, checkoutInfo.salesTaxToken)
            .then(result => {
                if(result.success) {
                    dispatch(getCreditCardKeySuccess(result.keyId, getJwtPayload(result.keyId), result.clientLibrary))
                }
                else {
                    dispatch(respondCheckoutTokenParamError(result.paramErrors))
                    dispatch(getCreditCardKeyFailure(result.errors, result.paramErrors))
                }

                return result.success
            })
            .catch(_ => {
                dispatch(getCreditCardKeyFailure(['Something went wrong while preparing the payment form. Please try again.']))

                return false
            })
    }
}

function completeOrderRequest() {
    return {
        type: actionTypes.COMPLETE_ORDER_REQUEST,
    }
}

function completeOrderSuccess(orderNumber, orderId) {
    return {
        type: actionTypes.COMPLETE_ORDER_SUCCESS,
        orderNumber,
        orderId,
    }
}

function completeOrderFailure(errors, paramErrors = {}) {
    return (dispatch, getState) => {
        if(hasParamError(paramErrors, ['shippingToken', 'salesTaxToken'])) {
            dispatch(salesTaxFailure(['Your shipping estimate has expired. Please try again.']))
        }

        if(hasParamError(paramErrors, 'paymentToken')) {
            if(paramErrors.paymentToken.some(x => ['card-already-authorized', 'card-declined', 'not-authorized', 'invalid-data'].includes(x.code))) {
                dispatch(clearPaymentTokens(paramErrors.paymentToken.map(x => x.message)))
            }
            else {
                dispatch(clearPaymentTokens(['Your payment has expired. Please try again.']))
            }
        }

        if(hasContactInfoParamErrors(paramErrors)) {
            dispatch(contactInfoFailure(['There were some problems with the values provided. Please see the error messages below.'], paramErrors))
        }

        dispatch({
            type: actionTypes.COMPLETE_ORDER_FAILURE,
            errors,
            paramErrors,
        })
    }
}

export function completeOrder(tokenCache) {
    return (dispatch, getState) => {
        const state = getState()

        const accountInfo = getAccountInfo(tokenCache.getAuth())
        const checkoutInfo = state.checkout.info

        const params = {
            contactName: checkoutInfo.contactName,
            contactEmail: checkoutInfo.contactEmail,
            contactPhone: checkoutInfo.contactPhone || get('phone', null, accountInfo),
            comments: checkoutInfo.comments,
        }

        const shippingToken = checkoutInfo.shippingMethod === SHIPPING_METHOD_HOME
            ? checkoutInfo.homeShippingEstimateToken
            : checkoutInfo.storePickupToken

        dispatch(completeOrderRequest())

        return api.completeOrder(tokenCache, state.config.orders.apiUrl, checkoutInfo.checkoutToken, shippingToken, checkoutInfo.salesTaxToken, checkoutInfo.paymentToken, params)
            .then(result => {
                if(result.success) {
                    dispatch(completeOrderSuccess(result.orderNumber, result.orderId))
                }
                else {
                    dispatch(respondCheckoutTokenParamError(result.paramErrors))
                    dispatch(completeOrderFailure(result.errors, result.paramErrors))
                }

                return result.success
            })
            .catch(_ => {
                dispatch(completeOrderFailure(['Something went wrong while completing your order. Please try again.']))

                return false
            })
    }
}

export function clearPaymentTokens(errors) {
    return {
        type: actionTypes.CLEAR_PAYMENT_TOKEN,
        errors,
    }
}

function checkGuestEmailRequest() {
    return {
        type: actionTypes.CHECK_GUEST_EMAIL_REQUEST,
    }
}

function checkGuestEmailSuccess(accountExists) {
    return {
        type: actionTypes.CHECK_GUEST_EMAIL_SUCCESS,
        accountExists,
    }
}

function checkGuestEmailFailure(errors, paramErrors = {}) {
    return {
        type: actionTypes.CHECK_GUEST_EMAIL_FAILURE,
        errors,
        paramErrors,
    }
}

export function checkGuestEmail(tokenCache, email) {
    return (dispatch, getState) => {
        const state = getState()

        dispatch(checkGuestEmailRequest())

        return api.checkGuestEmail(tokenCache, state.config.orders.apiUrl, {email})
            .then(result => {
                if(result.success) {
                    dispatch(checkGuestEmailSuccess(result.accountExists))

                    return {
                        success: true,
                        accountExists: result.accountExists,
                    }
                }
                else {
                    dispatch(checkGuestEmailFailure(result.errors, result.paramErrors))

                    return {
                        success: false,
                    }
                }
            })
            .catch(_ => {
                dispatch(checkGuestEmailFailure(['Something went wrong and this contact email could not be verified.']))

                return {
                    success: false,
                }
            })
    }
}
